import React, { useState, useEffect } from "react";
import Layout from "../pages/Layout";
import Advertise from "../pages/Advertise";

const Cac = () => {
  const [totalExpenses, setTotalExpenses] = useState(0); // Total marketing expenses
  const [newCustomers, setNewCustomers] = useState(0); // Number of new customers
  const [expenseUnit, setExpenseUnit] = useState("USD"); // Selected unit for expenses
  const [cac, setCac] = useState(0); // Calculated CAC

  // Event handlers for input changes
  const handleTotalExpensesChange = (e) => setTotalExpenses(parseFloat(e.target.value) || 0);
  const handleNewCustomersChange = (e) => setNewCustomers(parseInt(e.target.value) || 0);
  const handleExpenseUnitChange = (e) => setExpenseUnit(e.target.value);

  // Calculate CAC
  useEffect(() => {
    if (newCustomers > 0) {
      const calculatedCAC = totalExpenses / newCustomers;
      setCac(calculatedCAC);
    } else {
      setCac(0);
    }

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });

  }, [totalExpenses, newCustomers]);

  return (
    <Layout title="Customer Acquisition Cost (CAC) Calculator" path="cac">
      <section className="section-padding" id="cac-calculator">
        <div className="container mt-5">
          <div className="row">
            {/* CAC Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div className="p-4 border rounded shadow" style={{ border: "1px solid black" }}>
                <h3 className="text-center mb-4">Customer Acquisition Cost (CAC) Calculator</h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Input for Total Marketing Expenses */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="totalExpenses" className="form-label">
                        Total Marketing Expenses
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Total Marketing Expenses:</strong> The total cost spent on marketing efforts to acquire new customers, including advertisements, promotions, and campaigns."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="totalExpenses"
                        placeholder="Enter total marketing expenses"
                        value={totalExpenses || ""}
                        onChange={handleTotalExpensesChange}
                      />
                      <select
                        className="form-select mt-2"
                        value={expenseUnit}
                        onChange={handleExpenseUnitChange}
                      >
                        <option value="USD">USD</option>
                        <option value="INR">INR</option>
                        <option value="EUR">EUR</option>
                        <option value="GBP">GBP</option>
                        <option value="JPY">JPY</option>
                        <option value="AUD">AUD</option>
                      </select>
                    </div>

                    {/* Input for Number of New Customers */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="newCustomers" className="form-label">
                        Number of New Customers
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Number of New Customers:</strong> The number of customers acquired through marketing efforts in a given period."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="newCustomers"
                        placeholder="Enter number of new customers"
                        value={newCustomers || ""}
                        onChange={handleNewCustomersChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Customer Acquisition Cost (CAC):{" "}
                        <span className="fw-bold ms-3">
                          {expenseUnit}{" "}
                          {cac.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to Customer Acquisition Cost (CAC) Calculation
                </h3>

                {/* What is CAC? */}
                <h4 className="mt-3 mb-3">What is Customer Acquisition Cost?</h4>
                <p>
                  **Customer Acquisition Cost (CAC)** refers to the cost incurred to acquire a new customer. It is a key metric for measuring the efficiency of marketing and sales strategies.
                </p>

                {/* Why Use the CAC Calculator? */}
                <h4 className="mt-4 mb-3">Why Use the Customer Acquisition Cost (CAC) Calculator?</h4>
                <p>
                  This calculator simplifies the process of calculating CAC by considering total marketing expenses and the number of new customers acquired. It helps businesses understand their customer acquisition efficiency.
                </p>

                {/* Benefits of Using the CAC Calculator */}
                <h4 className="mt-4 mb-3">Benefits of Using the Customer Acquisition Cost (CAC) Calculator</h4>
                <ul>
                  <li>
                    <strong>Improves Budget Planning:</strong> Helps allocate marketing budgets effectively.
                  </li>
                  <li>
                    <strong>Assesses Marketing Effectiveness:</strong> Evaluates how well campaigns convert leads into customers.
                  </li>
                  <li>
                    <strong>Drives Cost Optimization:</strong> Encourages smarter spending to reduce CAC over time.
                  </li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">Who Should Use the CAC Calculator?</h4>
                <p>This calculator is suitable for:</p>
                <ul>
                  <li>
                    <strong>E-commerce Platforms:</strong> To evaluate the effectiveness of online ads and promotions.
                  </li>
                  <li>
                    <strong>Digital Marketers:</strong> To measure the ROI of marketing campaigns.
                  </li>
                  <li>
                    <strong>Online Retailers:</strong> To understand customer acquisition trends and cost drivers.
                  </li>
                </ul>

                {/* Additional Questions */}
                <h5 className="mt-3 mb-2">How does CAC impact profitability?</h5>
                <p>
                  High CAC reduces profit margins, especially for businesses with low customer lifetime value. Optimizing CAC ensures better profitability.
                </p>

                <h5 className="mt-3 mb-2">Can CAC vary across customer segments?</h5>
                <p>
                  Yes, CAC can differ by customer segment based on their acquisition channels and marketing costs. Businesses should analyze CAC for each segment to optimize strategies.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Cac;
